import React from "react";
import { cockpit } from "@/api";
import type { PositionsView, RequestDetail } from "@/api";
import { workspaceView } from "../../api/workspace-view";
import { positionProposalHydration } from "../screen-workspace.logic";

/** One read per basis at a time. Live refreshes join it and request one catch-up read;
 * they cannot repeatedly abandon the only response that would settle the panel. */
export function useWorkspacePositions(data: RequestDetail | null, scope: object, enabled: boolean) {
  const signature = data && enabled ? JSON.stringify([data.request.id, data.request.current_revision,
    data.request.version, data.request.engine_pin, data.draft?.id, data.decision?.id, data.decision?.action_safety, data.workflow?.result_id,
    data.workflow?.current, data.workflow?.reason, data.workflow?.processing, data.preparation.options]) : null;
  const latest = React.useRef({ data, scope, signature });
  latest.current = { data, scope, signature };
  const mounted = React.useRef(false);
  const inFlight = React.useRef<{ scope: object; signature: string; repeat: boolean } | null>(null);
  const [resolved, setResolved] = React.useState<{ scope: object; signature: string; value: PositionsView | null; failed: boolean } | null>(null);
  const [retry, setRetry] = React.useState(0);
  React.useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  React.useEffect(() => {
    const start = () => {
      const current = latest.current;
      if (!mounted.current || !current.signature || !current.data) return;
      const previous = inFlight.current;
      if (previous?.scope === current.scope && previous.signature === current.signature) {
        previous.repeat = true; return;
      }
      const read = { scope: current.scope, signature: current.signature, repeat: false };
      const detail = current.data;
      inFlight.current = read;
      const applies = () => mounted.current && inFlight.current === read
        && latest.current.scope === read.scope && latest.current.signature === read.signature;
      const options = detail.request.engine_pin.engine === "deterministic" && !detail.preparation.saved && detail.draft?.offer_snapshot
        ? positionProposalHydration(undefined, detail.draft) ?? undefined : undefined;
      void cockpit.getPositions(detail.request.id, options).then(value => {
        if (!applies()) return;
        if (!value || value.engine !== detail.request.engine_pin.engine) throw new Error("positions basis mismatch");
        const matches = value.engine !== "single_agent" || !value.result_id
          || value.result_id === detail.workflow?.result_id && workspaceView(detail).current;
        setResolved({ ...read, failed: false, value: matches ? value
          : { ...value, eligible: false, reason: "request_basis_stale", commercial: null, alternatives: [] } });
      }).catch(() => {
        if (applies()) setResolved(previous => ({ ...read, failed: true,
          // Keep the option controls and their local input visible; never retain failed-read
          // commercial content as an offer.
          value: previous?.scope === read.scope && previous.signature === read.signature && previous.value
            ? { ...previous.value, eligible: false, commercial: null, alternatives: [] } : null }));
      }).finally(() => {
        const follow = read.repeat && applies();
        if (inFlight.current === read) inFlight.current = null;
        // Publish the settled state before following an invalidation. New input always has
        // its own signature, so commercial content never crosses request/result revisions.
        if (follow) start();
      });
    };
    start();
  }, [data, scope, signature, retry]);
  const current = resolved?.scope === scope && resolved.signature === signature ? resolved : null;
  return { positions: current?.value ?? null, loading: Boolean(signature && !current),
    failed: current?.failed ?? false, refresh: () => setRetry(n => n + 1) };
}
